import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { playSfx } from "@/lib/sound";
import { Gift, Sparkles, Coins, Loader2, Terminal } from "lucide-react";
import { Countdown } from "@/components/Kit";

export default function Advent() {
  const { patchUser } = useOutletContext();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);

  const load = async () => { const { data } = await api.get("/advent"); setState(data); };
  useEffect(() => { load(); }, []);

  const claim = async () => {
    if (busy) return; setBusy(true); setLog(["> Initializing seasonal system log…"]);
    const lines = ["> Decrypting supply manifest…", "> Routing package through the Central Core…", "> Delivery authorized ✓"];
    lines.forEach((l, i) => setTimeout(() => setLog((p) => [...p, l]), 500 * (i + 1)));
    try {
      await new Promise((r) => setTimeout(r, 1800));
      const { data } = await api.post("/advent/claim");
      patchUser({ bits: data.wallet.bits, credits: data.wallet.bits, cosmium: data.wallet.cosmium });
      playSfx("win");
      setLog((p) => [...p, `> Received ${data.reward.bits} Bits + ${data.reward.cosmium} Cosmium${data.granted ? ` + ${data.granted}` : ""}`]);
      toast.success(`Advent package claimed! +${data.reward.bits} Bits, +${data.reward.cosmium} Cosmium`);
      load();
    } catch (e) { setLog((p) => [...p, `> ERROR: ${e.response?.data?.detail || "claim failed"}`]); toast.error(e.response?.data?.detail || "Already claimed today."); }
    finally { setBusy(false); }
  };

  if (!state) return null;
  return (
    <div className="max-w-lg mx-auto text-center">
      <h1 data-testid="advent-title" className="font-head font-black text-3xl sm:text-4xl neon-text mb-1">Advent Feed</h1>
      <p className="label-cap mb-6">Day {state.day} · Open one system log package per day</p>

      <div className="panel-glow p-8">
        <div className={`mx-auto w-28 h-28 rounded-2xl grid place-items-center mb-6 border-2 ${state.can_claim ? "border-primary bg-primary/10 animate-pulse" : "border-border bg-muted/30"}`}>
          <Gift className={`w-14 h-14 ${state.can_claim ? "text-primary" : "text-muted-foreground"}`} />
        </div>
        {log.length > 0 && (
          <div data-testid="advent-log" className="text-left bg-black/60 rounded-lg p-3 mb-5 font-mono text-xs text-green-400 space-y-1 min-h-[80px]">
            {log.map((l, i) => <div key={i}>{l}</div>)}
          </div>
        )}
        <div className="flex items-center justify-center gap-4 mb-6 text-sm">
          <span className="flex items-center gap-1 text-secondary"><Coins className="w-4 h-4" /> {state.reward.bits} Bits</span>
          <span className="flex items-center gap-1 text-[#5cc8ff]"><Sparkles className="w-4 h-4" /> {state.reward.cosmium} Cosmium</span>
        </div>
        {state.can_claim ? (
          <button data-testid="advent-claim-btn" onClick={claim} disabled={busy} className="btn-primary text-base px-10 py-4">
            {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Opening…</> : <><Terminal className="w-5 h-5" /> Open Today's Log</>}
          </button>
        ) : (
          <div data-testid="advent-cooldown" className="panel px-6 py-4 inline-block">
            <div className="label-cap">Next package in</div>
            <Countdown seconds={state.next_claim_in} className="font-head text-2xl font-black text-primary" onDone={load} />
          </div>
        )}
      </div>
    </div>
  );
}
